import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, Timestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { Bank, Account, AccountType, TransactionType, OptionalTransactionType, TransactionStatus, TransactionSource, AllTransactionType } from '../global/types';
import logger from '../utils/logger';
import _ from 'lodash';
import { SERVICE_URL } from '../global/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { uploadFile } from './supabaseService';
import { allSubcategories, getCategoryIdByName } from '../global/categories';
import { db } from '../firebaseConfig';
import { commitLedgerTransactionsBatchFn } from './ledgerServices';
import type { LedgerBatchResult } from './ledgerServices';

// imported here also because this module must not depend on App.tsx
dayjs.extend(utc);
dayjs.extend(timezone);

export type ImportResult = LedgerBatchResult;

export type StatementRecord = {
    id: string;
    date: string;
    amount: number;
    currency: string;
    description: string;
    card: number;
    hash: string;
    subCategory: string;
    category: string;
    subCategoryId: string;
};

export type StatementParseResponse = {
    transactions: StatementRecord[];
    subCategories: {
        id: string;
        name: string;
        category: string;
        isNew: boolean;
    }[];
};

export type StatementColumnMapping = {
    date: string | number;
    description: string | number;
    amount: string | number;
    balance?: string | number;
};

export type DetectStatementColumnsResponse = {
    ok: boolean;
    columns: string[];
    suggestedMapping?: Partial<Record<'date' | 'description' | 'amount' | 'balance', string | number>>;
    fileUrl?: string;
};

export type ExtractStatementFromColumnsResponse = {
    ok: boolean;
    count: number;
    resolvedColumnIndexes?: Partial<Record<'date' | 'description' | 'amount' | 'balance', number>>;
    transactions: any[];
    subCategories?: {
        id: string;
        name: string;
        category: string;
        isNew: boolean;
    }[];
};

export type StatementParsingFeedbackPayload = {
    userId: string;
    rating: 'good' | 'bad';
    comment?: string;
    extractedTransactionsCount: number;
    accountId?: string;
    fileName?: string;
};

export async function uploadStatementFile(file: File): Promise<string> {
    const fileUrl = await uploadFile(file);
    logger.debug('fileUrl', fileUrl);
    return fileUrl;
}

export async function parsePdfStatement(file: File, userId: string, bank: Bank = Bank.TBANK): Promise<string | null> {
    const fileUrl = await uploadFile(file);
    logger.debug('fileUrl', fileUrl);

    const response = await fetch(`${SERVICE_URL}/parse-statement?userId=${userId}&fileUrl=${fileUrl}&bank=${bank}`);

    if (!response.ok) {
        const errorText = await response.text();
        logger.error("[parsePdfStatement] HTTP error:", response.status, errorText);
        throw new Error(`Failed to parse statement: ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    const data = JSON.parse(text);

    if (!data.operationId) {
        console.error('[parsePdfStatement] No operationId in response. Full data:', data);
        throw new Error('No operation ID returned from server');
    }

    return data.operationId;
}

export async function parseUnknownPdfStatement(file: File, userId: string): Promise<string | null> {
    const fileUrl = await uploadFile(file);
    logger.debug('fileUrl', fileUrl);

    const response = await fetch(`${SERVICE_URL}/parse-statement-external?userId=${userId}&fileUrl=${fileUrl}`, {
        method: 'POST',
    });

    if (!response.ok) {
        const errorText = await response.text();
        logger.error("[parseUnknownPdfStatement] HTTP error:", response.status, errorText);
        throw new Error(`Failed to parse statement: ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    const data = JSON.parse(text);

    if (!data.operationId) {
        console.error('[parseUnknownPdfStatement] No operationId in response. Full data:', data);
        throw new Error('No operation ID returned from server');
    }

    return data.operationId;
}

export async function detectStatementColumns(fileUrl: string): Promise<DetectStatementColumnsResponse> {
    logger.debug('fileUrl', fileUrl);

    const formData = new FormData();
    formData.append('fileUrl', fileUrl);

    const response = await fetch(`${SERVICE_URL}/detect-statement-columns`, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorText = await response.text();
        logger.error('[detectStatementColumns] HTTP error:', response.status, errorText);
        throw new Error(`Failed to detect columns: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as DetectStatementColumnsResponse;
    if (!data?.ok) {
        throw new Error('Column detection failed');
    }

    return {
        ...data,
        fileUrl,
    };
}

export async function extractStatementFromColumns(fileUrl: string, mapping: StatementColumnMapping, categorize = true): Promise<ExtractStatementFromColumnsResponse> {
    logger.debug('fileUrl', fileUrl);

    const formData = new FormData();
    formData.append('fileUrl', fileUrl);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('categorize', categorize ? 'yes' : 'no');

    const response = await fetch(`${SERVICE_URL}/extract-statement-from-columns`, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorText = await response.text();
        logger.error('[extractStatementFromColumns] HTTP error:', response.status, errorText);
        throw new Error(`Failed to extract statement from columns: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as ExtractStatementFromColumnsResponse;
    if (!data?.ok) {
        throw new Error('Transaction extraction failed');
    }

    return data;
}

export async function submitStatementParsingFeedback(payload: StatementParsingFeedbackPayload): Promise<void> {
    await addDoc(collection(db, 'statementParsingFeedback'), {
        ...payload,
        comment: payload.comment?.trim() || '',
        source: 'pdf_column_mapping',
        createdAt: Timestamp.now(),
    });
}

type StatementDetails = {
    selectedAccountId: string;
    bank: Bank;
};

async function getAccount(selectedAccountId: string, bank: Bank, userId: string) {
    let accountData: Account | null = null;
    const accountDoc = await getDoc(doc(db, 'accounts', selectedAccountId));

    if (accountDoc.exists()) {
        accountData = accountDoc.data() as Account;
        accountData.id = selectedAccountId;
    } else {
        throw new Error(`Account with ID ${selectedAccountId} does not exist.`);
    }

    return accountData;
}


export async function uploadStatementRecords(userId: string, {
    selectedAccountId,
    bank
}: StatementDetails, records: StatementRecord[]): Promise<ImportResult> {
    const accountData = await getAccount(selectedAccountId, bank, userId);

    const recordsHashsMap = _.keyBy(records, 'hash');
    const allHashes = _.keys(recordsHashsMap);
    const existingDocs = await Promise.all(
        allHashes.map(hash => getDoc(doc(db, 'ledger_transactions', hash)))
    );

    console.log(`[DeepDebug] Total Records: ${records.length}, Existing Docs found: ${existingDocs.filter(d => d.exists()).length}`);

    // Identify records that exist but have invalid dates (e.g. < 2000)
    const invalidDateRecordsHashes = existingDocs
        .filter((snap) => {
            if (!snap.exists()) return false;
            const data = snap.data();
            // Check if date is invalid (before year 2000)
            const isInvalid = data.date && dayjs(data.date.toDate()).year() < 2000;
            if (isInvalid) {
                console.log(`[DeepDebug] Found BAD EXISTING record: ${snap.id} with date ${data.date.toDate()}`);
            }
            return isInvalid;
        })
        .map((snap) => snap.id);

    console.log(`[DeepDebug] Invalid Date Records to Overwrite: ${invalidDateRecordsHashes.length}`);

    const existingValidRecordsHashes = existingDocs
        .filter((snap) => snap.exists() && !invalidDateRecordsHashes.includes(snap.id))
        .map((snap) => snap.id);

    console.log(`[DeepDebug] Valid Existing Records (Skipping): ${existingValidRecordsHashes.length}`);

    // Process new records AND existing records with invalid dates
    // [DeepDebug] FORCING OVERWRITE: Ignoring duplicate check to ensure defective records are fixed.
    const recordsToProcessHashes = allHashes;
    console.log(`[DeepDebug] Final Records to Process (FORCED): ${recordsToProcessHashes.length}`);

    const subCategoryIds = new Set<string>();
    const transactions: Record<string, unknown>[] = recordsToProcessHashes.map((recordHash) => {
        const record: StatementRecord = recordsHashsMap[recordHash];
        const amount = Math.abs(record.amount);
        let parsedDate = dayjs.tz(record.date, dayjs.tz.guess()).toDate();

        // Safety check for invalid dates (NaN or < 2000)
        if (isNaN(parsedDate.getTime()) || dayjs(parsedDate).year() < 2000) {
            console.warn(`[DeepDebug] FIXING Invalid Date (${record.date} -> ${parsedDate}) for record ${record.description}`);
            parsedDate = new Date();
        }

        // --- IMPROVED CATEGORY MAPPING ---
        // "Needs" is the 50/30/20 template's word, not a category group: defaulting to it gave the row
        // an invalid categoryGroup and meant the "Other" fallback below could never match a group.
        const recordGroup = (record.category as AllTransactionType) || OptionalTransactionType.EXPENSES;
        let categoryId = '';
        let categoryName = record.subCategory || '';

        // Normalize group for comparison (handling SAVING vs SAVINGS)
        const normalizedGroup = recordGroup.toUpperCase();

        if (record.subCategory) {
            // 1. Try exact match by name
            const exactMatch = allSubcategories.find(c => c.name.toLowerCase() === record.subCategory.toLowerCase());
            if (exactMatch) {
                categoryId = exactMatch.id;
                categoryName = exactMatch.name;
                console.log(`[Categorization] Exact match for "${record.subCategory}": ${categoryName} (${categoryId})`);
            } else {
                // 2. Try fuzzy match (within group if possible)
                const fuzzyMatch = allSubcategories.find(c => {
                    const catGroup = c.group?.toUpperCase();
                    const isSameGroup = catGroup === normalizedGroup ||
                        (normalizedGroup === 'SAVINGS' && catGroup === 'SAVING') ||
                        (normalizedGroup === 'SAVING' && catGroup === 'SAVINGS');

                    return isSameGroup && (
                        c.name.toLowerCase().includes(record.subCategory.toLowerCase()) ||
                        record.subCategory.toLowerCase().includes(c.name.toLowerCase())
                    );
                });

                if (fuzzyMatch) {
                    categoryId = fuzzyMatch.id;
                    categoryName = fuzzyMatch.name;
                    console.log(`[Categorization] Fuzzy match for "${record.subCategory}" in ${recordGroup}: ${categoryName} (${categoryId})`);
                } else {
                    // 3. Fallback to "Other [Group]"
                    const otherInGroup = allSubcategories.find(c => {
                        const catGroup = c.group?.toUpperCase();
                        const isSameGroup = catGroup === normalizedGroup ||
                            (normalizedGroup === 'SAVINGS' && catGroup === 'SAVING') ||
                            (normalizedGroup === 'SAVING' && catGroup === 'SAVINGS');

                        return isSameGroup && (c.name.toLowerCase().includes('other') || c.name.toLowerCase().includes('others'));
                    });

                    if (otherInGroup) {
                        categoryId = otherInGroup.id;
                        categoryName = otherInGroup.name;
                        console.log(`[Categorization] Fallback to Other for "${record.subCategory}" in ${recordGroup}: ${categoryName} (${categoryId})`);
                    } else {
                        console.warn(`[Categorization] NO MATCH for "${record.subCategory}" in group ${recordGroup}`);
                    }
                }
            }
        }

        if (categoryId) {
            subCategoryIds.add(categoryId);
        }
        // ---------------------------------

        // Determine transaction type
        const isIncome = record.category === TransactionType.INCOME ||
            (record.category === TransactionType.TRANSFERS && record.amount > 0);
        const type = isIncome ? TransactionType.INCOME : TransactionType.EXPENSE;

        return {
            id: recordHash,
            accountId: accountData.id,
            importReferenceId: recordHash,
            currency: record.currency || undefined,
            amount,
            date: parsedDate.getTime(),
            type,
            categoryId,
            categoryName: categoryName || record.subCategory || 'Uncategorized',
            categoryGroup: recordGroup,
            description: record.description,
            status: TransactionStatus.POSTED,
            source: TransactionSource.BANK_IMPORT
        };
    });

    // Create subCategoryIds in the user's categories if they don't exist
    const batch = writeBatch(db);
    const categoriesMap = _.keyBy(allSubcategories, "id");
    for (const subCategoryId of subCategoryIds) {
        const category: any = categoriesMap[subCategoryId];
        if (category) {
            batch.set(doc(db, 'settings', userId, category.group.toLowerCase(), subCategoryId), {
                id: subCategoryId,
                name: category.name,
            });
        }
    }
    await batch.commit();

    // The server denominates each line in the account and snapshots it at the line's own day;
    // writing these from here would leave them with no snapshot, hence out of every converted total.
    const { data } = await commitLedgerTransactionsBatchFn({ rows: transactions });

    // balance is kept in sync by the onLedgerTransactionWrite Cloud Function reacting to the
    // ledger_transactions writes above — writing it here too would double-count this import.
    await updateDoc(doc(db, 'accounts', accountData.id), { updatedAt: Timestamp.now() });

    return data;
}

export async function categorizeStatementRecords(transactions: any[], userId: string): Promise<StatementRecord[]> {
    const response = await fetch(`${SERVICE_URL}/classify-transactions?userId=${userId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transactions }),
    });
    const text = await response.text();
    return JSON.parse(text).transactions;
}

